import { useEffect, useState } from "react";
import { Box, Input } from "@chakra-ui/react";
import {
  BaseStack,
  GoiBaseBottomSheet,
  ColorIcon,
  NextLink,
  colors,
  DirectEventPathType,
  customEvent,
} from "goi_common";
import { baseColors, baseSpacing } from "goi_common/src/design-system";
import { formatPhoneNumber } from "goi_common/src/utils/parsePhoneNumber";
import { sessionStorage, storageKeys } from "@/utils/storage";
import { useConsultSubmit } from "@/hooks/useConsultSubmit";
import { buildCallLeadMemo } from "@/utils/buildCallLeadMemo";
import ConsultCompleteModal from "@/components/consult/ConsultCompleteModal";

type Step = "reason" | "phone";

const REASON_ITEMS = [
  "상품 설명을 더 자세히 듣고 싶어요.",
  "다른 상조와 어떤 차이가 있는지 알고 싶어요.",
  "전화로 가입하고 싶어요.",
  "기타 문의가 있어요.",
];

interface ConsultBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onCompleted?: () => void;
  onSubmitSuccess?: () => void;
  hasReasonStep: boolean;
  allowSkipAgreement?: boolean;
  mainColor?: string;
  path?: keyof typeof DirectEventPathType;
  showCostSavingVariant?: boolean;
  showVerificationVariant?: boolean;
  titleOverride?: string;
  descriptionOverride?: string;
  trigger: string;
  defaultReason?: string;
}

export default function ConsultBottomSheet({
  isOpen,
  onClose,
  onCompleted,
  onSubmitSuccess,
  hasReasonStep = true,
  allowSkipAgreement = false,
  mainColor = baseColors.primary_orange,
  path = "call_lead",
  showCostSavingVariant = false,
  showVerificationVariant = false,
  titleOverride,
  descriptionOverride,
  trigger,
  defaultReason,
}: ConsultBottomSheetProps) {
  const initialStep: Step = hasReasonStep ? "reason" : "phone";
  const [step, setStep] = useState<Step>(initialStep);
  const [reason, setReason] = useState("");
  const [phone, setPhone] = useState("");
  const [privateAgree, setPrivateAgree] = useState(false);
  const [marketingAgree, setMarketingAgree] = useState(false);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);

  const sessionPhone = sessionStorage.get(storageKeys.Phone) as string;
  const agreement = JSON.parse(
    sessionStorage.get(storageKeys.Agreement) || "{}"
  );
  const { 전화, 문자, 이메일 } = agreement;
  const isAllMarketingAgreed = 전화 && 문자 && 이메일;
  const canSkipAgreementStep = allowSkipAgreement && !!isAllMarketingAgreed;

  useEffect(() => {
    if (isOpen) {
      setStep(initialStep);
      setReason("");
      setPhone(sessionPhone ? formatPhoneNumber(sessionPhone) : "");
      setPrivateAgree(false);
      setMarketingAgree(false);
    }
  }, [isOpen, initialStep, sessionPhone]);

  const { loading, submitConsult } = useConsultSubmit({
    path,
    onSuccess: () => {
      onClose();
      setIsCompleteModalOpen(true);
      onSubmitSuccess?.();
    },
  });

  const handleReasonNext = () => {
    if (!reason) {
      alert("궁금한 내용을 선택해주세요.");
      return;
    }
    customEvent({
      newGtm: { path: "call_lead", trigger_and_target: "click_reason_next" },
    });
    // 동의 스킵 가능하면 바로 제출, 아니면 전화번호 단계
    if (canSkipAgreementStep && !!sessionPhone) {
      submitConsult({
        phone: sessionPhone,
        memo: buildCallLeadMemo({
          trigger,
          reason: reason || defaultReason,
        }),
        marketingAgree: true,
      });
    } else {
      setStep("phone");
    }
  };

  const handlePhoneSubmit = () => {
    const rawPhone = phone.replaceAll("-", "");
    if (!rawPhone) {
      alert("핸드폰 번호를 입력해주세요.");
      return;
    }
    if (!/^010[1-9]\d{7}$/.test(rawPhone)) {
      alert("핸드폰 번호 형식을 확인해주세요.");
      return;
    }
    if (!privateAgree) {
      alert("개인정보 수집 및 이용에 동의해주세요.");
      return;
    }
    if (loading) return;

    if (showVerificationVariant) {
      customEvent({
        newGtm: {
          path: "call_lead",
          trigger_and_target: "submit_consult_btm_verification",
        },
      });
    }

    submitConsult({
      phone: rawPhone,
      memo: buildCallLeadMemo({
        trigger,
        reason: reason || defaultReason,
      }),
      marketingAgree,
    });
  };

  const isReasonStep = step === "reason";

  const header = isReasonStep ? (
    <BaseStack spacing={baseSpacing.sp_8}>
      <Box className="headline_1_sb" style={{ whiteSpace: "pre-line" }}>
        {"궁금한 내용이 있으신가요?\n장례 전문 상담원이 도와드려요"}
      </Box>
      <Box className="body_1_normal_r" color={baseColors.label_alternative_1}>
        도움이 필요한 내용을 체크하고, [다음] 버튼을 눌러주세요.
      </Box>
    </BaseStack>
  ) : showVerificationVariant ? (
    <BaseStack spacing={baseSpacing.sp_8}>
      <Box className="headline_1_sb">휴대폰 본인인증 도움 받기</Box>
      <Box className="body_1_normal_r" color={baseColors.label_alternative_1}>
        지금 휴대폰 본인인증 도움 받으면,
        <br />
        <span style={{ color: mainColor, fontWeight: 700 }}>
          장례 비용 최대 800만원
        </span>{" "}
        아낄 수 있어요.
      </Box>
    </BaseStack>
  ) : (
    <BaseStack spacing={baseSpacing.sp_8}>
      <Box className="headline_1_sb" style={{ whiteSpace: "pre-line" }}>
        {titleOverride ??
          "통화 가능한 휴대폰 번호 입력 및\n필수 약관에 동의해 주세요"}
      </Box>
      <Box
        className="body_1_normal_r"
        color={baseColors.label_alternative_1}
        style={{ whiteSpace: "pre-line" }}
      >
        {descriptionOverride ?? (
          <>
            상담을 위해 전화를 드리는 용도 외에는
            <br />
            고객님의 전화번호를 사용하지 않아요.
          </>
        )}
      </Box>
    </BaseStack>
  );

  const footer = isReasonStep ? (
    <GoiBaseBottomSheet.Footer
      primaryButton={{
        text: canSkipAgreementStep && !!sessionPhone ? "상담 신청하기" : "다음",
        onClick: handleReasonNext,
      }}
    />
  ) : (
    <GoiBaseBottomSheet.Footer
      primaryButton={{
        text: loading
          ? "신청 중..."
          : showVerificationVariant
          ? "동의하고 본인인증 도움 받기"
          : showCostSavingVariant
          ? "동의하고 장례 비용 아끼는 방법 알아보기"
          : "상담 신청하기",
        onClick: handlePhoneSubmit,
      }}
    />
  );

  return (
    <>
      <GoiBaseBottomSheet
        isOpen={isOpen}
        onClose={onClose}
        showCloseButton={true}
        mainColor={mainColor}
        header={header}
        footer={footer}
      >
        <Box padding="0 20px">
          {isReasonStep && (
            <BaseStack spacing={4}>
              {REASON_ITEMS.map((item) => (
                <BaseStack
                  key={item}
                  direction="horizontal"
                  spacing={12}
                  alignItems="center"
                  padding="12px 0"
                  style={{ cursor: "pointer" }}
                  onClick={() => setReason(item)}
                >
                  <Box
                    width="24px"
                    height="24px"
                    borderRadius="50%"
                    bg={reason === item ? colors.gray900 : colors.gray300}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    <ColorIcon
                      name="icon-check-mono"
                      color={baseColors.white}
                      size={22}
                    />
                  </Box>
                  <Box
                    className="body_1_normal_m"
                    color={baseColors.label_alternative_1}
                  >
                    {item}
                  </Box>
                </BaseStack>
              ))}
            </BaseStack>
          )}

          {!isReasonStep && (
            <BaseStack spacing={baseSpacing.sp_16}>
              <BaseStack spacing={baseSpacing.sp_8}>
                <Box className="label_1_sb" color={baseColors.label_normal}>
                  전화번호
                </Box>
                <Input
                  value={phone}
                  onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
                  placeholder="010-1234-5678"
                  type="tel"
                  maxLength={13}
                  bg={baseColors.fill_normal}
                  border="none"
                  borderRadius="8px"
                  height="48px"
                  _focus={{ border: "none", boxShadow: "none" }}
                />
              </BaseStack>

              <BaseStack spacing={baseSpacing.sp_4}>
                {/* 전체 동의하기 */}
                <BaseStack
                  direction="horizontal"
                  spacing={12}
                  alignItems="center"
                  padding="4px 0"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    const all = privateAgree && marketingAgree;
                    setPrivateAgree(!all);
                    setMarketingAgree(!all);
                  }}
                >
                  <Box
                    width="24px"
                    height="24px"
                    borderRadius="50%"
                    bg={
                      privateAgree && marketingAgree
                        ? colors.gray900
                        : colors.gray300
                    }
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    <ColorIcon
                      name="icon-check-mono"
                      color={baseColors.white}
                      size={22}
                    />
                  </Box>
                  <Box
                    className="body_3_reading_sb"
                    color={baseColors.label_normal}
                  >
                    전체 동의하기
                  </Box>
                </BaseStack>

                <Box height="1px" bg={baseColors.line_normal} />

                {/* 필수: 개인정보 */}
                <BaseStack
                  direction="horizontal"
                  justifyContent="space-between"
                  alignItems="center"
                  style={{ cursor: "pointer" }}
                  onClick={() => setPrivateAgree((v) => !v)}
                >
                  <BaseStack
                    direction="horizontal"
                    spacing={8}
                    alignItems="center"
                  >
                    <Box padding="6px" flexShrink={0}>
                      <ColorIcon
                        name="icon-check-mono"
                        color={
                          privateAgree
                            ? baseColors.label_normal
                            : baseColors.line_accent
                        }
                        size={20}
                      />
                    </Box>
                    <Box
                      className="label_1_r"
                      color={baseColors.label_alternative_1}
                    >
                      (필수)개인정보의 수집 및 이용에 대한 동의
                    </Box>
                  </BaseStack>
                  <NextLink href="https://goifuneral.notion.site/f6e4472bcfd548d6bd5ac672fd84db27">
                    <ColorIcon
                      name="icon-arrow-right-small-mono"
                      size={16}
                      color={baseColors.label_assistive_1}
                    />
                  </NextLink>
                </BaseStack>

                {/* 선택: 마케팅 */}
                <BaseStack
                  direction="horizontal"
                  justifyContent="space-between"
                  alignItems="center"
                  style={{ cursor: "pointer" }}
                  onClick={() => setMarketingAgree((v) => !v)}
                >
                  <BaseStack
                    direction="horizontal"
                    spacing={8}
                    alignItems="center"
                  >
                    <Box padding="6px" flexShrink={0}>
                      <ColorIcon
                        name="icon-check-mono"
                        color={
                          marketingAgree
                            ? baseColors.label_normal
                            : baseColors.line_accent
                        }
                        size={20}
                      />
                    </Box>
                    <Box
                      className="label_1_r"
                      color={baseColors.label_alternative_1}
                    >
                      (선택)마케팅 정보 수신 동의
                    </Box>
                  </BaseStack>
                  <NextLink href="https://goifuneral.notion.site/3d74caff865d48ccbf32d9a0ad3df80b">
                    <ColorIcon
                      name="icon-arrow-right-small-mono"
                      size={16}
                      color={baseColors.label_assistive_1}
                    />
                  </NextLink>
                </BaseStack>
              </BaseStack>
            </BaseStack>
          )}
        </Box>
      </GoiBaseBottomSheet>

      <ConsultCompleteModal
        isOpen={isCompleteModalOpen}
        onClose={() => {
          setIsCompleteModalOpen(false);
          onCompleted?.();
        }}
      />
    </>
  );
}
